import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {LegibilityStatus} from '@Libraries/event-colors.library';
import type {StylesLegibilityHintProps} from './styles-legibility-hint.types';

/**
 * The one indication of a legibility problem that survives closing the styles panel.
 *
 * The banner inside the panel disappears with the panel, and the palette is already live by then —
 * so without this a page whose colours fail can be carried to the save with no sign of it.
 *
 * `ADJUSTED` is deliberately absent. That state reports a change we made *for* the user and is
 * already resolved; repeating it outside the panel would be noise on a perfectly legible page.
 */
export function StylesLegibilityHint({status, onClick, onFix, className = ''}: StylesLegibilityHintProps): ReactElement | null {
  /**
   * A sibling of the clickable message rather than a child of it: nesting a button inside the
   * ClickableDiv would make one click both fix the colours and reopen the panel.
   *
   * `POOR` only — a `WARNING` is a Mode A fill with no derived alternative to offer, so there is
   * nothing for a Fix action to do.
   */
  const renderFixAction = (): ReactElement | null => {
    if (status !== LegibilityStatus.POOR) {
      return null;
    }
    return <Button type={ButtonType.PRIMARY} size={ButtonSize.SMALL} text={window.i18next.t('pmwjs_fix_legibility')} onClick={onFix} />;
  };

  if (status !== LegibilityStatus.POOR && status !== LegibilityStatus.WARNING) {
    return null;
  }

  const toneClass = status === LegibilityStatus.POOR ? 'ct-error-primary' : 'ct-warning-primary';
  const messageKey = status === LegibilityStatus.POOR ? 'pmwjs_content_colors_reducing_legibility' : 'pmwjs_content_color_hard_to_see_on_page';

  return (
    <div className={`flexbox flex-items-center gap-2 ${className}`}>
      <ClickableDiv className="flexbox flex-items-center gap-2 flex-1" onClick={onClick}>
        <Icon icon="icon-exclamation-circle" size={IconSize.SIZE_ICON_12} className={`${toneClass} flex-shrink-0`} />
        <Text val={window.i18next.t(messageKey)} size={TextSize.SM} className={`${toneClass} flex-1`} />
      </ClickableDiv>
      {renderFixAction()}
    </div>
  );
}
